import { FileData } from '@/types'
import { toastError, toastSuccess } from './toast'

/**
 * Gets the MIME type for a certificate file based on its extension
 */
const getCertificateMimeType = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase()
  const mimeTypes: Record<string, string> = {
    p12: 'application/x-pkcs12',
    pfx: 'application/x-pkcs12',
    pem: 'application/x-pem-file',
    crt: 'application/x-x509-ca-cert',
    cer: 'application/x-x509-ca-cert',
  }
  return mimeTypes[extension || ''] || 'application/octet-stream'
}

/**
 * Downloads a certificate file from base64 data
 */
export const downloadCertificate = (certificate: FileData): void => {
  if (!certificate.data || !certificate.filename) {
    toastError('Aucun certificat à télécharger')
    return
  }

  try {
    // Decode base64 to binary
    const binaryString = atob(certificate.data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }

    const mimeType = getCertificateMimeType(certificate.filename)
    const blob = new Blob([bytes], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = certificate.filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toastSuccess('Certificat téléchargé avec succès')
  } catch (error) {
    toastError('Erreur lors du téléchargement du certificat')
  }
}

/**
 * Extracts and formats certificate content for display
 * For PEM files, returns the decoded text content
 * For binary certificates, returns the base64 data
 */
export const getCertificateContent = (certificate: FileData): string => {
  if (!certificate.data) return ''

  try {
    // Decode base64
    const binaryString = atob(certificate.data)
    // Check if it's a text-based certificate (PEM format)
    if (binaryString.includes('-----BEGIN') || binaryString.includes('-----END')) {
      return binaryString
    }
    // For binary certificates, return base64 data
    return certificate.data
  } catch {
    // If decoding fails, return base64 data as fallback
    return certificate.data
  }
}

